import { ref } from 'vue';
import axios from 'axios';
import { useToast } from '@/composables/useToast';

export const HERA_CASSETTE_LABEL_MAX = 2000;

export const canPrintHeraCassetteLabel = ({
    theme,
    sheetName,
    canView = true,
    cassetteId,
}) => theme === 'Hera'
&& sheetName === 'hc'
&& canView !== false
&& cassetteId !== null
    && cassetteId !== undefined;

export const buildHeraCassetteLabelPayload = ({
    selectAllMatching,
    selectedIds = [],
    excludedIds = [],
    filters = {},
}) => {
    if (selectAllMatching) {
        return {
            scope: 'all',
            filters,
            ...(excludedIds.length ? { excluded: [...new Set(excludedIds)] } : {}),
        };
    }

    return {
        scope: 'selection',
        selection: [...new Set(selectedIds)],
    };
};

export const countHeraCassetteLabelSelection = ({
    selectAllMatching,
    selectedIds = [],
    excludedIds = [],
    totalItems = 0,
}) => selectAllMatching
        ? Math.max(0, Number(totalItems) - new Set(excludedIds).size)
        : new Set(selectedIds).size;

const errorMessage = (error, fallback) => {
    const validation = error?.response?.data?.errors;
    const firstValidation = validation ? Object.values(validation).flat()[0] : null;

    return firstValidation ?? error?.response?.data?.message ?? fallback;
};

const downloadFromUrl = (url) => {
    if (!url || typeof document === 'undefined') return;

    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    link.remove();
};

export function useHeraCassetteLabelPrinting({
    client = axios,
    toast = useToast(),
    download = downloadFromUrl,
} = {}) {
    const isPrinting = ref(false);

    const printSingle = async (cassetteId) => {
        if (!cassetteId || isPrinting.value) return null;

        isPrinting.value = true;
        try {
            const { data } = await client.post(`/api/hera/cassettes/${cassetteId}/print-jobs`);
            const job = data?.data;

            if (!job?.pdf_url) {
                throw new Error('PDF etichetta non disponibile.');
            }

            download(job.pdf_url);
            toast.info('PDF etichetta generato.');

            return job;
        } catch (error) {
            toast.error(errorMessage(error, 'Impossibile generare l’etichetta PDF.'));
            return null;
        } finally {
            isPrinting.value = false;
        }
    };

    const queueBulk = async (payload) => {
        if (!payload || isPrinting.value) return null;

        isPrinting.value = true;
        try {
            const { data } = await client.post('/api/hera/cassette-print-jobs', payload);
            const job = data?.data;
            const labelCount = Number(job?.item_count ?? 0);
            const labels = labelCount === 1 ? '1 etichetta' : `${labelCount} etichette`;

            toast.info(`Generazione di ${labels} avviata. Riceverai una notifica quando il PDF sarà pronto.`, {
                autoClose: 6000,
            });

            return job;
        } catch (error) {
            toast.error(errorMessage(error, 'Impossibile avviare la generazione delle etichette.'));
            return null;
        } finally {
            isPrinting.value = false;
        }
    };

    return {
        isPrinting,
        printSingle,
        queueBulk,
    };
}